import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import { Check, X, Users, Info } from "lucide-react";
import { styled } from "@mui/material/styles";
import { Profile } from "server/Authentication/OutputsTypes";
import toast from "react-hot-toast";

// Hooks & utils
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { highlightText, timeAgo, exerciseTitle } from "components/common/utils";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

// Types & server calls
import {
  getSubmissions,
  getSubmissionsForTeacher,
} from "server/StartupAdmins CRUD/Requests";
import {
  rejectExercise,
  updateCorrectorOrSupervisor,
} from "server/StartupAdmins CRUD/Requests";
import { ApiResponse } from "server";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import {
  RejectExerciseInput,
  UpdateCorrectorOrSupervisorInput,
} from "server/StartupAdmins CRUD/InputsTypes";
import { ExerciseStatus, SubmissionType } from "types";

// Modals
import SubmissionDetailsModal from "./submissionDetails";
import SubmitCorrectionModal from "./submitCorrectionModal";

// === STYLES DE L'ONGLET PERSONNALISÉ ===
const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: theme.typography.pxToRem(14),
  minHeight: "48px",
  padding: "8px 16px",
  [theme.breakpoints.down("sm")]: {
    fontSize: theme.typography.pxToRem(12),
    padding: "6px 12px",
    minHeight: "40px",
  },
  "&.Mui-selected": {
    backgroundColor: "#E3B505",
    color: "black",
  },
}));

// === DÉTERMINATION DU RÔLE (CORRECTOR OU CONTROLLER) ===
type TeacherRole = "corrector" | "controller";

/**
 * Détermine le rôle d'un user sur une soumission.
 * @param submission
 * @param userId
 */
function getUserRoleForSubmission(
  submission: GetSubmissionsOutput,
  userId: string,
): TeacherRole {
  return submission.correctorId === userId ? "corrector" : "controller";
}

// === HOOK POUR RÉCUPÉRER LES SOUMISSIONS (EXEMPLE) ===
function useSubmissions() {
  const [submissions, setSubmissions] = React.useState<
    GetSubmissionsOutput[] | null
  >(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchSubmissions = async () => {
    try {
      const response: ApiResponse<GetSubmissionsOutput[]> =
        await getSubmissionsForTeacher();
      if (response.status === 200 && Array.isArray(response.body)) {
        setError(null);
        setSubmissions(response.body);
      } else {
        setError("Erreur lors de la récupération des soumissions.");
      }
    } catch (err) {
      setError("Une erreur s'est produite lors de la connexion au serveur.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSubmissions();
  }, []);

  return { submissions, loading, error, fetchSubmissions };
}

// === ITEM INDIVIDUEL D'UNE SOUMISSION ===
interface SubmissionListItemProps {
  submission: GetSubmissionsOutput;
  index: number;
  searchQuery: string;
  onViewDetails: (submission: GetSubmissionsOutput) => void;
  onSubmitCorrection: (submission: GetSubmissionsOutput) => void;
  role: TeacherRole;
}

const SubmissionListItem: React.FC<SubmissionListItemProps> = ({
  submission,
  index,
  searchQuery,
  onViewDetails,
  onSubmitCorrection,
  role,
}) => {
  const [showActions, setShowActions] = React.useState(false);

  // Exemples de conditions
  const canBeCorrected =
    submission.status === ExerciseStatus.PROCESSING &&
    !submission.idConversation;
  const teacherCanSubmitCorrection = role === "corrector";

  return (
    <div
      className="flex flex-col sm:grid sm:grid-cols-12 sm:gap-4 p-4 bg-white rounded-lg shadow-sm hover:bg-gray-50 relative group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Mobile: Layout en colonne, Desktop: Layout en grille */}
      <div className="flex-1 mb-3 sm:mb-0 sm:col-span-5">
        <Typography variant="body1" className="font-bold text-sm sm:text-base">
          {index + 1}. {highlightText(exerciseTitle(submission), searchQuery)}
        </Typography>
      </div>

      <div className="flex justify-between items-center sm:col-span-7">
        {/* Date */}
        <div className="sm:col-span-2">
          <Typography
            variant="body2"
            className="text-gray-500 text-xs sm:text-sm"
          >
            {timeAgo(new Date(submission.createdAt))}
          </Typography>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 sm:col-span-5 sm:justify-end">
          {canBeCorrected && teacherCanSubmitCorrection && (
            <IconButton
              onClick={() => onSubmitCorrection(submission)}
              className="text-purple-600 hover:bg-purple-50"
              size="small"
              title="Soumettre la correction"
              sx={{ width: 40, height: 40 }}
            >
              <Check size={18} />
            </IconButton>
          )}

          <IconButton
            onClick={() => onViewDetails(submission)}
            className="text-gray-600 hover:bg-gray-100"
            size="small"
            title="Voir les détails"
            sx={{ width: 40, height: 40 }}
          >
            <Info size={18} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

// === COMPOSANT PRINCIPAL AVEC LES TABS ET LES FILTRES ===
export default function SubmissionTabs() {
  const { submissions, loading, error, fetchSubmissions } = useSubmissions();

  const [tabValue, setTabValue] = React.useState(0);
  const [selectedType, setSelectedType] = React.useState("");
  const [searchQuery, setSearchQuery] = React.useState("");

  // États pour les modals
  const [openDetailsModal, setOpenDetailsModal] = React.useState(false);
  const [selectedSubmission, setSelectedSubmission] =
    React.useState<GetSubmissionsOutput | null>(null);

  const [openAcceptModal, setOpenAcceptModal] = React.useState(false);
  const [openRejectModal, setOpenRejectModal] = React.useState(false);
  const [openAssignModal, setOpenAssignModal] = React.useState(false);
  const [openCorrectionModal, setOpenCorrectionModal] = React.useState(false);

  const [selectedSubmissionId, setSelectedSubmissionId] = React.useState<
    string | null
  >(null);

  // Récupération du user connecté pour savoir si c'est un correcteur sur telle soumission
  const user = useAuthUser<any>();
  const userId = user?._id || "";

  // Responsive
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Gère le changement de tab
  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Gère le filtre par type
  const handleSubmissionTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedType(event.target.value);
  };

  // Gère la recherche
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // == API: Rejeter la soumission ==
  const handleReject = async (submissionId: string, reason: string) => {
    const input: RejectExerciseInput = { observation: reason };
    const promise = rejectExercise(submissionId, input);

    toast.promise(promise, {
      loading: "Rejet en cours...",
      success: (res) => {
        if (res.status === 200) {
          fetchSubmissions();
          setOpenRejectModal(false);
          return "Soumission rejetée avec succès.";
        } else {
          throw new Error(
            (res.body as { message: string }).message ||
              "Une erreur est survenue lors du rejet.",
          );
        }
      },
      error: (err) => err.message || "Impossible de rejeter la soumission.",
    });
  };

  // == API: Assigner corrector / supervisor ==
  const handleAssign = async (
    submissionId: string,
    assignment: { teacher: string; controller: string },
  ) => {
    const input: UpdateCorrectorOrSupervisorInput = {
      correctorId: assignment.teacher,
      supervisorId: assignment.controller,
    };
    const promise = updateCorrectorOrSupervisor(submissionId, input);

    toast.promise(promise, {
      loading: "Assignation en cours...",
      success: (res) => {
        if (res.status === 200) {
          fetchSubmissions();
          setOpenAssignModal(false);
          return "Assignation réussie !";
        } else {
          throw new Error(
            (res.body as { message: string }).message ||
              "Une erreur est survenue lors de l'assignation.",
          );
        }
      },
      error: (err) => err.message || "Erreur lors de l'assignation.",
    });
  };

  // == Ouvrir la modal de détails ==
  const handleViewDetails = (submission: GetSubmissionsOutput) => {
    setSelectedSubmission(submission);
    setOpenDetailsModal(true);
  };

  // == Ouvrir la modal de soumission de correction (pour correcteur) ==
  const handleSubmitCorrection = (submission: GetSubmissionsOutput) => {
    setSelectedSubmission(submission);
    setOpenCorrectionModal(true);
  };

  // == Filtrer les soumissions en fonction du tab, du type et de la recherche ==
  const getFilteredSubmissions = () => {
    if (!submissions) return [];

    let filtered = submissions;

    // Filtre par status selon l'onglet
    switch (tabValue) {
      case 0:
        filtered = filtered.filter(
          (s) => s.status === ExerciseStatus.PROCESSING && !s.idConversation,
        );
        break;
      case 1:
        filtered = filtered.filter(
          (s) => s.status === ExerciseStatus.PROCESSING && s.idConversation,
        );
        break;
      case 2:
        filtered = filtered.filter((s) => s.status === ExerciseStatus.ANSWERED);
        break;
      default:
        break;
    }

    // Filtre par type (control, blocage, unclear, etc.)
    if (selectedType) {
      filtered = filtered.filter((s) => s.submissionType === selectedType);
    }

    // Filtre par recherche (titre)
    if (searchQuery) {
      filtered = filtered.filter((s) =>
        exerciseTitle(s).toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Tri par date desc
    return filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  };

  // == ÉTATS DE CHARGEMENT / ERREUR ==
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // == RENDER PRINCIPAL ==
  return (
    <>
      <Box
        sx={{
          maxWidth: "full",
          margin: "auto",
          bgcolor: "background.paper",
          p: { xs: 1, sm: 2 },
        }}
      >
        {/* Barre d'onglets + Filtres */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              aria-label="soumissions"
              textColor="inherit"
              TabIndicatorProps={{ style: { display: "none" } }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
                "& .MuiTabs-scrollButtons.MuiTabs-scrollButtonsDesktop": {
                  display: { xs: "flex", sm: "flex" },
                },
              }}
            >
              <CustomTab value={0} label="En attente" />
              <CustomTab value={1} label="En cours de revue" />
              <CustomTab value={2} label="Traitées" />
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {/* Barre de recherche */}
            <input
              type="text"
              placeholder="Rechercher..."
              className="w-full px-3 sm:px-4 py-2 focus:outline-none rounded-lg shadow-sm bg-gray-100 border border-gray-200 text-sm"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {/* Sélecteur de type */}
            <FormControl
              variant="standard"
              sx={{
                minWidth: { xs: "100%", sm: 200 },
                "& .MuiInput-root": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            >
              <Select
                value={selectedType}
                onChange={handleSubmissionTypeChange}
                displayEmpty
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  "& .MuiSelect-select": {
                    padding: { xs: "8px 12px", sm: "12px 16px" },
                  },
                }}
              >
                <MenuItem
                  value=""
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Tous les types
                </MenuItem>
                <MenuItem
                  value="control"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Contrôle
                </MenuItem>
                <MenuItem
                  value="blocage"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Blocage
                </MenuItem>
                <MenuItem
                  value="unclear"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Non démarré
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Liste des soumissions filtrées */}
        <div className="space-y-2">
          {getFilteredSubmissions().length === 0 ? (
            <p className="text-gray-500 text-center text-sm">
              Aucune soumission trouvée.
            </p>
          ) : (
            getFilteredSubmissions().map((submission, index) => {
              // Déterminer le rôle corrector/controller pour l'utilisateur
              const role = getUserRoleForSubmission(submission, userId);

              return (
                <SubmissionListItem
                  key={submission._id}
                  submission={submission}
                  index={index}
                  searchQuery={searchQuery}
                  onViewDetails={(s) => handleViewDetails(s)}
                  onSubmitCorrection={handleSubmitCorrection}
                  role={role}
                />
              );
            })
          )}
        </div>
      </Box>

      {/* MODALS */}
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: 4, zIndex: 9999 }}
      >
        {/* Détails */}
        {selectedSubmission && (
          <SubmissionDetailsModal
            open={openDetailsModal}
            onClose={() => {
              setOpenDetailsModal(false);
              setSelectedSubmission(null);
              fetchSubmissions();
            }}
            submission={selectedSubmission}
          />
        )}
        {/* Soumission de correction (si role = corrector) */}
        {selectedSubmission && (
          <SubmitCorrectionModal
            open={openCorrectionModal}
            onClose={() => {
              setOpenCorrectionModal(false);
              setSelectedSubmission(null);
              fetchSubmissions();
            }}
            submission={selectedSubmission}
          />
        )}
      </Box>
    </>
  );
}
